import {type Raid, type Resource} from "@/raids/rest/RaidSchema.ts";
import {Avatar, Button, Card, List, Select, Skeleton, Tooltip} from "antd";
import {PlusCircleOutlined, StopOutlined} from "@ant-design/icons";
import Strav from "@/assets/resources/stravidium.webp";
import Titan from "@/assets/resources/titanium.webp";
import Spice from "@/assets/resources/spice-sand.webp";
import {useEffect, useRef, useState} from "react";
import {InputNumberLocalized} from "@/components/InputNumberLocalized.tsx";
import {localizedNumberFormatter} from "@/helpers/numberFormatHelper.tsx";

type Props = {
  raid: Raid | null,
  isLoading: boolean,
  isSaving: boolean,
  isEditAble: boolean,
  callBackUpdateResources: (list: Resource[]) => void;
  onFocusInput: (inputKey: string) => void;
}

export function RaidResourcePanel({raid, isLoading, isSaving, isEditAble, callBackUpdateResources, onFocusInput}: Props) {

  const amountInputRef = useRef<HTMLInputElement>(null);
  const restoreResourceAmountFocusAfterSaveRef = useRef(false);
  const defaultResourceType: Resource['type'] = "SPICE";
  const [resourceType, setResourceType] = useState<Resource['type']>(defaultResourceType);
  const [resourceAmount, setResourceAmount] = useState<string | null>(null);

  const [raidResources, setResources] = useState<Resource[]>([]);
  const skeletonResources = [{
    amount: 1,
    type: "SPICE"
  },
    {
      amount: 1,
      type: "SPICE"
    },
    {
      amount: 1,
      type: "SPICE"
    },] as Resource[];

  useEffect(() => {
    if (!raid) return;
    setResources(raid.resources);
  }, [raid]);

  useEffect(() => {
    if (isSaving) return;
    if (!restoreResourceAmountFocusAfterSaveRef.current) return;
    restoreResourceAmountFocusAfterSaveRef.current = false;
    requestAnimationFrame(() => {
      amountInputRef.current?.focus({ preventScroll: true });
    });
  }, [isSaving]);

  const canAddResources =
    isEditAble && raid !== null && raid.status !== "CLOSED";

  /* Resource 2 Asset mapper*/
  function getResourceAsset(resourceType: Resource['type']) {
    switch (resourceType) {
      case "STRAVIDIUM":
        return Strav;
      case "TITANIUM":
        return Titan;
      case "SPICE":
        return Spice;
    }
  }

  function addResource() {
    if (isSaving) return;
    if (!raid || raid.status === "CLOSED") return;
    const amount = resourceAmount === null ? null : Number(resourceAmount);

    if (amount === null || !Number.isFinite(amount) || amount === 0) {
      setResourceAmount(null);
    } else {
      const newResource = {
        type: resourceType,
        amount,
      } as Resource;
      const changedResource = [...raidResources, newResource];
      setResourceAmount(null);
      setResourceType(defaultResourceType);
      restoreResourceAmountFocusAfterSaveRef.current = true;
      callBackUpdateResources(changedResource);
    }
  }

  function removeResource(item: Resource) {
    if (!item) return;
    let removed = false;
    const newResources = raidResources.filter((x) => {
      if (!removed && x.amount === item.amount && x.type === item.type) {
        removed = true; // nur das erste Matching wird entfernt
        return false;
      }
      return true;
    });
    callBackUpdateResources(newResources);
  }

  /* Resource Selector*/
  const resourceSelect = (
      <Select
          className={"mx-0.25! min-w-28!"}
          disabled={isSaving || !canAddResources}
          value={resourceType}
          onChange={(e) => setResourceType(e)}
          allowClear
          options={[
            {value: "SPICE", label: 'Spice'},
            {value: "TITANIUM", label: 'Titanium'},
            {value: "STRAVIDIUM", label: 'Stravidium'},
          ]}
          placeholder="Resource"
      />
  );

  return (
      <Card title={"Ressourcen"}
            className={
              "flex min-h-[400px] flex-none flex-col overflow-hidden " +
              "md:min-h-0 md:flex-1 md:h-full md:flex-[0_0_250px] lg:flex-[0_0_300px]"
            }
            classNames={{
              body: "flex min-h-0 flex-1 flex-col overflow-hidden",
            }}
      >
        <div className={"grid min-h-0 flex-1 grid-rows-[auto_1fr] gap-2"}>
          <div className={"w-full shrink-0 [&_.ant-input-number-group-addon]:p-0!"}>
            {isLoading && <Skeleton.Input className={"w-full! block!"} active/>}
            {!isLoading && canAddResources &&
                <InputNumberLocalized
                    ref={amountInputRef}
                    key={"add-resource"}
                    data-focus-key={"add-resource"}
                    data-tour="add-resource"
                    disabled={isSaving || !canAddResources}
                    value={resourceAmount}
                    stringMode

                    max={10000000}      //max-Wert 10.000.000
                    maxLength={10}      // 10 Mio brauchen 10 zeichen, also maximal-länge: 10
                    onChange={(e) => {
                      // When using stringMode, AntD gives string | null
                      setResourceAmount((e ?? null) as string | null)
                    }}
                    onPressEnter={() => {
                      addResource();
                    }}
                    onFocus={() => {
                      onFocusInput("add-resource");
                    }}
                    keyboard
                    addonBefore={resourceSelect}
                    addonAfter={
                      <Button color="default"
                              disabled={isSaving || !canAddResources}
                              className={"h-7!"}
                              variant="text"
                              icon={<PlusCircleOutlined/>}
                              loading={isSaving}
                              onClick={() => {
                                addResource();
                              }}
                      />
                    }
                    placeholder={"Menge"}
                />
            }
          </div>
          <div className={"min-h-0 overflow-hidden"}>
            <div
                className={"max-h-full min-h-0 h-full overflow-y-auto border border-[var(--ant-color-bg-base)] bg-[var(--ant-color-bg-container)]"}>
              <List<Resource>
                  className={"bg-transparent"}
                  itemLayout="horizontal"
                  dataSource={isLoading ? skeletonResources : raidResources}
                  locale={{
                    emptyText: "Ressourcen sammeln"
                  }}
                  renderItem={(item) => (
                      <List.Item className={"p-2!"}
                                 actions={
                                   !isLoading ? [
                                     isEditAble && raid?.status === "RUNNING" && (
                                         <Tooltip title={"Ressource entfernen"}>
                                           <Button
                                               color={"default"}
                                               variant="filled"
                                               icon={<StopOutlined/>}
                                               onClick={() => {
                                                 removeResource(item);
                                               }}
                                           />
                                         </Tooltip>
                                     )
                                   ].filter(Boolean) : []
                                 }
                      >
                        <Skeleton loading={!isSaving && isLoading} active avatar={{
                          shape: "circle"
                        }} paragraph={false}>
                          <List.Item.Meta
                              avatar={<Avatar src={getResourceAsset(item.type)}/>}
                              title={localizedNumberFormatter(item.amount)}
                          />
                        </Skeleton>
                      </List.Item>
                  )}
              />
            </div>
          </div>
        </div>
      </Card>
  )
}